<template>
  <div>
    <Teleport v-if="isDialogOpen" to="body">
      <div
        class="fixed inset-0 z-50 bg-surface-950/10 dark:bg-surface-950/40 pointer-events-none opacity-0 animate-[fade-in_0.3s_ease_forwards]"
      ></div>
    </Teleport>
    <Dialog
      v-if="isDialogOpen"
      id="quick-task-dialog-wrapper"
      ref="wrapper"
      v-model:visible="isTaskUiOpen"
      position="center"
      :maximize-icon="
        isTaskUiFullscreen
          ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center'
          : 'pi pi-arrow-up-right-and-arrow-down-left-from-center'
      "
      maximizable
      :style="{
        maxWidth: isTaskUiFullscreen ? '100%' : 'min(90vw,48rem)',
        width: '100%',
        // marginBottom: isTaskUiFullscreen ? '0' : '1rem',
        height: isTaskUiFullscreen ? '100%' : 'min(90vh,40rem)',
      }"
      :pt-options="{
        mergeProps: ptViewMerge,
      }"
      class="bg-surface-0 dark:bg-surface-800 overflow-hidden"
      :pt="{
        header:
          'flex border-b border-surface-100 dark:border-surface-700 pb-2 pt-2! px-3 @csm/task-dialog:px-4 not-supports-cq:sm:px-4 @clg/task-dialog:px-16 not-supports-cq:lg:px-4 @cxl/task-dialog:px-24 not-supports-cq:xl:px-4 @c2xl/task-dialog:px-32 not-supports-cq:2xl:px-4',
        pcCloseButton: 'dark:text-surface-0 quick-task-close-button',
        pcMaximizeButton: `${isTaskUiFullscreen ? 'quick-task-minimize-btn' : 'quick-task-maximize-btn'}`,
        root: 'border dark:border-surface-700 rounded-xl task-dialog-root @container/task-dialog relative z-50',
        content: 'px-2! pt-2 flex flex-col pb-0! overflow-y-auto p-maximized:grow',
        headerActions: 'flex gap-2 items-center shrink-0',
      }"
      @maximize="toggleTaskUiFullscreen(true)"
      @unmaximize="toggleTaskUiFullscreen(false)"
    >
      <template #header>
        <div class="flex grow">
          <QuickTaskHeading v-model="filter" />
        </div>
      </template>
      <template #default>
        <div ref="inner" class="grow flex flex-col items-center w-full pb-4">
          <div class="relative grow w-full flex flex-col items-center">
            <QuickTaskResult
              v-show="taskResult && !contentEditData && !newTaskSTatus.hasError"
              :before-change="flipping ? beforeChange : undefined"
              :after-change="flipping ? afterChange : undefined"
              class="w-full"
            />
            <QuickTaskEdit
              v-if="contentEditData"
              class="w-full"
              :before-change="flipping ? beforeChange : undefined"
              :after-change="flipping ? afterChange : undefined"
            />
            <QuickTaskOptions
              v-else-if="!currentTaskKey && !newTaskId"
              :filter="filter"
              :before-select="flipping ? beforeChange : undefined"
              :after-select="flipping ? afterChange : undefined"
              class="w-full"
              :recent-task-keys="recentTasks"
            />
            <QuickTaskPrompt
              v-else-if="!taskResult || newTaskSTatus.hasError"
              :before-change="flipping ? beforeChange : undefined"
              :after-change="flipping ? afterChange : undefined"
              class="w-full"
              @add-recent-task="addRecentTask"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import Dialog from "@/volt/Dialog.vue";
import QuickTaskPrompt from "./meta/QuickTaskPrompt.vue";
import QuickTaskOptions from "./meta/QuickTaskOptions.vue";
import Flipping from "flipping/dist/flipping.web";
import radiusPlugin from "flipping/lib/plugins/radius";
import QuickTaskResult from "./meta/QuickTaskResult.vue";
import QuickTaskHeading from "./meta/QuickTaskHeading.vue";
import QuickTaskEdit from "./meta/QuickTaskEdit.vue";
import { ptViewMerge } from "@/volt/utils";

let initialized = false;
const inner = ref<HTMLElement | null>(null);
const {
  taskResult,
  isTaskUiOpen,
  toggleTaskUiFullscreen,
  toggleTaskUi,
  isTaskUiFullscreen,
  currentTaskKey,
  newTaskId,
  newTaskSTatus,
  contentEditData,
} = useQuickTask();
const filter = ref("");
const flipping = shallowRef<Flipping | null>(null);
const keys = useMagicKeys();
const recentTasks = useLocalStorage<string[]>("quick-task-recent-tasks", []);

const isDialogOpen = computed(
  () =>
    isTaskUiOpen.value &&
    !currentSessionInformation.initializingSession &&
    !!currentSessionInformation.appAuthToken
);

function addRecentTask(key: string) {
  recentTasks.value = [...new Set([key, ...recentTasks.value])].slice(0, 3);
}

function beforeChange() {
  flipping.value?.read();
}

function afterChange() {
  nextTick(() => {
    flipping.value?.flip();
  });
}

function handleKeyboardShortcut() {
  toggleTaskUi();
  setTimeout(() => {
    sendDynamicGtmAnalyticsEvent(
      isTaskUiOpen.value
        ? analyticsEvents.QUICK_TASK.quick_task_keyboard_open
        : analyticsEvents.QUICK_TASK.quick_task_keyboard_close
    );
  }, 100);
}

whenever(keys.meta_k, handleKeyboardShortcut);
whenever(keys.control_k, handleKeyboardShortcut);

watch(inner, newVal => {
  if (!newVal) return;
  const wrapper = document.querySelector(".task-dialog-root");
  if (!wrapper) return;
  if (initialized) return;
  initialized = true;
  nextTick(() => {
    flipping.value = new Flipping({
      parentElement: wrapper,
      duration: 400,
      plugins: [radiusPlugin],
    });
    flipping.value.read();
  });
});

watch(taskResult, (newData, oldData) => {
  if (newData && !oldData) {
    currentTaskKey.value = newData.taskType;
  }
});
</script>
